import { useHubFrame } from '../hub/HubFrameContext';
import React from 'react';
import styled from 'styled-components';
import { type Auth } from 'firebase/auth';

import { useAuth } from '../firebase/FirebaseContext';
import { useYouTabBadge } from '../hooks/useYouTabBadge';
// v4 preview (?v4=1): the flag-ON You-tab badge uses account-state dot semantics
// (dot = save-boards, count = notifications). Flag-OFF renders the legacy count
// badge below, byte-identical to today's production.
import { isV4Preview } from '../lib/v4Preview';
import { V4MobileTabBar } from '../v4/V4MobileTabBar';

import { useMobileTabBarVisibility } from './mobileTabBarVisibility';
import { MobileTabBar } from './MobileTabBar';

/**
 * Smart wrapper for the bottom tab bar.
 *
 * Gates render on "user has at least one recipient" — meaning at least
 * one board has been created. Subscribes to the same Firestore recipient
 * collection BrowseMyFriendsSection uses, so the visibility flips on
 * the same edge.
 *
 * Mounted at the bottom of the homepage so the tab bar appears once the
 * user is "in the product." Calendar page mounts MobileTabBar directly
 * (unconditional) because if you're on /calendar, the tab bar has to
 * be there.
 *
 * The gate itself lives in `mobileTabBarVisibility.ts`, not inline here: the
 * promoted `/liked/:recipientId` board shows its header sign-in door on mobile
 * only when this bar is NOT rendering, so that page has to ask the same question
 * this component answers. One predicate, two readers — see that module.
 */

interface MobileTabBarMountProps {
  active: 'people' | 'calendar' | 'guides' | 'you';
  /** Test override for the auth instance. */
  authInstance?: Auth;
  /** Pre-decided visibility: skip the live read and use this instead. A test
   *  override, and also how a caller that already evaluated
   *  `useMobileTabBarVisibility` (LikedPage's promoted shell, which keys its
   *  header door on the complement) drives the bar from that ONE evaluation
   *  rather than a second one that could disagree for a frame. */
  forceVisible?: boolean;
}

// Reserve vertical space at the bottom of the page when the bar is shown,
// so fixed/sticky content doesn't overlap the last row of tiles.
const Spacer = styled.div`
  /* Matches the bar's height so the feed's last row clears it. The bar drops
     its safe-area padding in a browser tab (see MobileTabBar), so the spacer
     drops it too — both restore it together in standalone (home-screen) mode. */
  height: 72px;
  @media (display-mode: standalone) {
    height: calc(72px + env(safe-area-inset-bottom));
  }
`;

export const MobileTabBarMount: React.FC<MobileTabBarMountProps> = ({
  active,
  authInstance,
  forceVisible,
}) => {
  const ctxAuth = useAuth();
  const auth = authInstance ?? ctxAuth;
  const youBadge = useYouTabBadge();

  // The gate, and the reasoning behind each clause, live in
  // mobileTabBarVisibility.ts. While auth/hydration is in flight that helper
  // reports `visible: false` (plus `resolving: true`, which only the header-door
  // fallback cares about) — flashing the bar in and out is worse than showing it
  // slightly later.
  const { visible: liveVisible } = useMobileTabBarVisibility(auth);
  const visible = forceVisible ?? liveVisible;

  const hubFrame = useHubFrame();
  if (hubFrame) return null;

  if (!visible) return null;

  return (
    <>
      <Spacer aria-hidden="true" />
      {isV4Preview() ? (
        <V4MobileTabBar active={active} />
      ) : (
        <MobileTabBar active={active} youBadge={youBadge} />
      )}
    </>
  );
};

export default MobileTabBarMount;
